'use client';

import { searchPharmacies } from 'actions/Sunfire';
import { useAppDispatch, useAppSelector } from 'lib/hooks';
import { ChangeEvent, useCallback, useState } from 'react';
import { addPharmacy } from 'slices/pharmacySlice';
import { addPharmacy as addDbPharmacy, saveLocation } from 'actions/Database';
import styles from 'styles/sunfire.forms.module.css';
import { Pharmacy } from 'types/SunFire';
import { formatAddress } from 'utils/formatters';
import AddCircleIcon from 'components/icons/AddCircleIcon';
import { isZipCode } from 'utils/formHelpers';
import {
  selectCurrentLocation,
  selectZipCode,
  setLocation,
} from 'slices/locationSlice';
import { ANALYTICS_EVENTS, capture } from 'lib/analytics';

interface PharmacySearchProps {
  title: string;
  subtitle: string;
}

const PharmacySearch = ({ title, subtitle }: PharmacySearchProps) => {
  const dispatch = useAppDispatch();
  const userZip = useAppSelector(selectZipCode);
  const currentLocation = useAppSelector(selectCurrentLocation);

  const [zipCode, setZipCode] = useState<string>(userZip);
  const [name, setName] = useState<string>('');
  const [results, setResults] = useState<[]>([]);

  const zipLabel = 'ZIP Code';

  const handleLocationChange = useCallback(
    async (e: ChangeEvent<HTMLInputElement>) => {
      const newZip = e.target.value;
      setZipCode(newZip);
      if (isZipCode(newZip)) {
        const updatedLocation = { ...currentLocation, zipcode: newZip };
        dispatch(setLocation(updatedLocation));
        try {
          await saveLocation(updatedLocation);
        } catch {
          // ignore — zip updated locally even if DB save fails
        }
      }
    },
    [dispatch, currentLocation]
  );

  const handleNameChange = async (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setName(value);

    if (value.length > 2) {
      capture(ANALYTICS_EVENTS.pharmacySearchPerformed);
      const results = await searchPharmacies(name, zipCode);
      setResults(results);
    } else {
      setResults([]);
    }
  };

  const clearState = () => {
    setName('');
    setResults([]);
  };

  const savePharmacy = async (pharmacy: Pharmacy) => {
    const dbPharmacy = await addDbPharmacy(pharmacy);

    dispatch(addPharmacy({ ...dbPharmacy, created_at: null }));
    capture(ANALYTICS_EVENTS.pharmacySaved);
    clearState();
  };

  return (
    <form className="has-divider">
      <fieldset>
        <div className="form-header">
          <legend>{title}</legend>
          <p className="explanation-text">{subtitle}</p>
        </div>
        <div className="row">
          <div className="one-third-column">
            <label htmlFor="pharmacy-zip" className="field-label">
              {zipLabel}
            </label>
            <input
              id="pharmacy-zip"
              type="search"
              className="input"
              name="location"
              defaultValue={zipCode}
              autoComplete="off"
              onChange={handleLocationChange}
              placeholder="Enter 5-digit ZIP code"
            />
          </div>
          <div className="two-third-column">
            <label htmlFor="pharmacy-name" className="field-label">
              Pharmacy Name
            </label>
            <input
              type="search"
              name="name"
              className="input"
              placeholder="Start typing a pharmacy name to search..."
              value={name}
              autoComplete="off"
              onChange={handleNameChange}
              disabled={!isZipCode(zipCode)}
            />
            {results.length > 0 && (
              <menu className={styles.autocomplete}>
                {results.map((pharmacy: Pharmacy, idx: number) => {
                  return (
                    <li key={idx}>
                      <button
                        type="button"
                        className={styles.autocompleteBtn}
                        onClick={() => savePharmacy(pharmacy)}
                      >
                        <div>
                          <p>
                            <b>{pharmacy.name}</b>
                          </p>
                          <p>{formatAddress(pharmacy)}</p>
                        </div>
                        <AddCircleIcon />
                      </button>
                    </li>
                  );
                })}
              </menu>
            )}
          </div>
        </div>
      </fieldset>
    </form>
  );
};

export default PharmacySearch;
